import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import DashboardPage from './pages/dashboard/DashboardPage';
import InventoryPage from './pages/inventory/InventoryPage';
import AddProductPage from './pages/inventory/AddProductPage';
import EditProductPage from './pages/inventory/EditProductPage';
import OrdersPage from './pages/orders/OrdersPage';
import OrderDetailsPage from './pages/orders/OrderDetailsPage';
import CommunicationPage from './pages/communication/CommunicationPage';
import ProfilePage from './pages/profile/ProfilePage';

// Components
import ProtectedRoute from './components/auth/ProtectedRoute';
import NotificationsPage from './pages/notifications/NotificationsPage';

import PorterPage from './components/porter/PorterPage';
import HelpPage from './pages/help/HelpPage';
import BillingPage from './pages/billing/BillingPage';
import BillingReportsPage from './pages/billing/BillingReportsPage';
import InvoiceDetailsPage from './pages/billing/InvoiceDetailsPage';

// Returns Pages - ADD THIS IMPORT
import ReturnsPage from './pages/returns/ReturnsPage';
import ViewProductPage from './components/inventory/ViewProductPage';
import ViewPurchaseInvoicePage from './pages/billing/ViewPurchaseInvoicePage';
import InvoiceCreationPage from './pages/billing/InvoiceCreationPage';
import CustomersPage from './pages/customers/CustomersPage';
import SuppliersPage from './pages/suppliers/SuppliersPage';
import ViewQuotationPage from './pages/billing/ViewQuotationPage';
import VerifyEmail from './pages/VerifyEmail';



// Import spares admin components/pages (from the new subfolder)
// Correct imports after renaming files to Spares*.jsx
import SparesDashboard from './spares-admin/pages/SparesDashboard';
import SparesPorters from './spares-admin/pages/SparesPorters';
import SparesTasks from './spares-admin/pages/SparesTasks';
import SparesCreateTask from './spares-admin/pages/SparesCreateTask';
import SparesProducts from './spares-admin/pages/SparesProducts';
import SparesCashDeposits from './spares-admin/pages/SparesCashDeposits';
import SparesReports from './spares-admin/pages/SparesReports';
import SparesSettings from './spares-admin/pages/SparesSettings';
import SparesInvoices from './spares-admin/pages/SparesInvoices';
import SparesTaskDetails from './spares-admin/pages/SparesTaskDetails';

import SparesSidebar from './spares-admin/components/SparesSidebar';
import SparesHeader from './spares-admin/components/SparesHeader';



const SparesAdminPanel = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);

  return (
  <div className="min-h-screen flex bg-gray-50">
  {/* Sidebar stays fixed visually */}
  <SparesSidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />

  {/* Right panel */}
  <div className="flex-1 flex flex-col h-screen overflow-hidden">
    {/* Header scrolls with content */}
    <div className="shrink-0">
      <SparesHeader toggleSidebar={() => setSidebarOpen(!sidebarOpen)} />
    </div>

    {/* Only this area scrolls */}
    <main className="flex-1 overflow-y-auto bg-gray-50 p-6">
      <Routes>
            <Route path="/" element={<SparesDashboard />} />
            <Route path="dashboard" element={<SparesDashboard />} />
            <Route path="porters" element={<SparesPorters />} />
            <Route path="tasks" element={<SparesTasks />} />
            <Route path="create-task" element={<SparesCreateTask />} />
            <Route path="products" element={<SparesProducts />} />
            <Route path="cash-deposits" element={<SparesCashDeposits />} />
            <Route path="reports" element={<SparesReports />} />
            <Route path="tasks/:taskId" element={<SparesTaskDetails />} />
            <Route path="invoices" element={<SparesInvoices />} />
            <Route path="settings" element={<SparesSettings />} />
            <Route path="*" element={<Navigate to="dashboard" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <NotificationProvider>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="/porter" element={<PorterPage />} />
            <Route path="/help" element={<HelpPage />} />

            <Route path="/verify-email" element={<VerifyEmail />} />

            <Route path="/customers" element={<ProtectedRoute><CustomersPage /></ProtectedRoute>} />


            <Route path="/suppliers" element={<ProtectedRoute><SuppliersPage /></ProtectedRoute>} />


            {/* Protected Routes */}
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            } />

            <Route path="/inventory" element={
              <ProtectedRoute>
                <InventoryPage />
              </ProtectedRoute>
            } />

            <Route path="/inventory/add" element={
              <ProtectedRoute>
                <AddProductPage />
              </ProtectedRoute>
            } />

            <Route path="/inventory/edit/:id" element={
              <ProtectedRoute>
                <EditProductPage />
              </ProtectedRoute>
            } />

            <Route path="/orders" element={
              <ProtectedRoute>
                <OrdersPage />
              </ProtectedRoute>
            } />

            <Route path="/orders/:id" element={
              <ProtectedRoute>
                <OrderDetailsPage />
              </ProtectedRoute>
            } />

            <Route path="/communication" element={
              <ProtectedRoute>
                <CommunicationPage />
              </ProtectedRoute>
            } />

            <Route path="/billing" element={
              <ProtectedRoute>
                <BillingPage />
              </ProtectedRoute>
            } />
            <Route path="/billing/purchase/:id" element={
              <ProtectedRoute>
                <ViewPurchaseInvoicePage />
              </ProtectedRoute>
            } />



            <Route path="/billing/quotation/:id" element={
              <ProtectedRoute>
                <ViewQuotationPage />
              </ProtectedRoute>
            } />



            <Route path="/billing/reports" element={
              <ProtectedRoute>
                <BillingReportsPage />
              </ProtectedRoute>
            } />

            <Route path="/billing/invoice/:id" element={
              <ProtectedRoute>
                <InvoiceDetailsPage />
              </ProtectedRoute>
            } />

            {/* ADD THIS RETURNS ROUTE */}
            <Route path="/returns" element={
              <ProtectedRoute>
                <ReturnsPage />
              </ProtectedRoute>
            } />

            <Route path="/profile" element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            } />

            <Route path="/billing/create" element={
              <ProtectedRoute>
                <InvoiceCreationPage />
              </ProtectedRoute>
            } />


            <Route path="/inventory/view/:id" element={
              <ProtectedRoute>
                <ViewProductPage />
              </ProtectedRoute>
            } />
{/* Add this for spares admin */}
  <Route path="/spares-admin/*" element={
    <ProtectedRoute>
      <SparesAdminPanel />
    </ProtectedRoute>
  } />
  
  {/* Existing catch-all */}
  <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </NotificationProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;